import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigurationService } from '@/shared/services/configuration.service';
import { environment } from 'src/environments/environment';
import { EMPTY, Observable, defer, merge, of, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, map, mergeMap, shareReplay, take, tap } from 'rxjs/operators';
import { setPortalJson } from '@/shared/utils/portal-storage';

@Injectable({
  providedIn: 'root'
})
export class SharedLinkRegionService {
  private readonly sharedPlantRequestByLinkShareUuid = new Map<string, Observable<{ regionId: string; plantData: any }>>();

  constructor(
    private http: HttpClient,
    private configService: ConfigurationService
  ) {}

  getSharedPlantDataAutoRegion(linkShareUuid: string): Observable<{ regionId: string; plantData: any }> {
    const cached = this.sharedPlantRequestByLinkShareUuid.get(linkShareUuid);
    if (cached) return cached;

    const request$ = defer(() => {
      const configuredRegions: string[] = Array.isArray(environment.config?.regionsList)
        ? environment.config.regionsList
        : [];

      const defaultRegion: string = environment.config?.defaultRegion ?? '1';

      const regionsToTry = Array.from(new Set([defaultRegion, ...configuredRegions]))
        .filter((regionId) => Boolean(environment.config?.[regionId]?.cpmUrl))
        .slice(0, 2);

      const requests = regionsToTry.map((regionId) => {
        const baseUrl = environment.config[regionId].cpmUrl;
        return this.http.get(`${baseUrl}shared/plant/${linkShareUuid}`).pipe(
          map((plantData) => ({ regionId, plantData })),
          catchError(() => EMPTY)
        );
      });

      return merge(...requests).pipe(
        defaultIfEmpty(null),
        take(1),
        mergeMap((result) => {
          if (!result) {
            return throwError(() => new Error('Shared link not found in any configured region.'));
          }
          return of(result);
        }),
        tap(({ regionId }) => {
          this.configService.setRegionId(regionId);
          setPortalJson('siteRegion', regionId);
        })
      );
    }).pipe(
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.sharedPlantRequestByLinkShareUuid.set(linkShareUuid, request$);
    return request$;
  }
}
